/**
 * Notification Sidebar — right-hand panel with full notification feed.
 * Replaces the old dropdown notification panel.
 */
import { openNotificationUrl } from './notification-link.js';
import { api, router, store, timeAgo } from '@isg/ui-kit';
import { t } from '../i18n/index.js';
import { showNotificationPopup } from './notification-popup.js';

let pollInterval = null;
let staleCheckInterval = null;
let isOpen = false;
let sidebarEl = null;
let lastSuccessfulPoll = Date.now();
let bellEl = null;
const STALE_THRESHOLD_MS = 60_000;

// Inject CSS for stale-indicator dot once.
function ensureStaleStyles() {
  if (document.getElementById('notif-stale-styles')) return;
  const style = document.createElement('style');
  style.id = 'notif-stale-styles';
  style.textContent = `
    .toolbar-notification-btn { position: relative; }
    .toolbar-notification-btn.stale::after {
      content: '';
      position: absolute;
      top: 4px;
      right: 4px;
      width: 8px;
      height: 8px;
      border-radius: 50%;
      background: #f59e0b;
      border: 1.5px solid var(--surface-primary, #fff);
      pointer-events: none;
    }
  `;
  document.head.appendChild(style);
}

function updateStaleIndicator() {
  if (!bellEl) return;
  const isStale = (Date.now() - lastSuccessfulPoll) > STALE_THRESHOLD_MS;
  if (isStale) {
    bellEl.classList.add('stale');
    bellEl.setAttribute('title', t('notifications.sidebar.stale'));
  } else {
    bellEl.classList.remove('stale');
    bellEl.removeAttribute('title');
  }
}

const BELL_SVG = '<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9"/><path d="M13.73 21a2 2 0 0 1-3.46 0"/></svg>';

function escHtml(str) {
  const d = document.createElement('div');
  d.textContent = str || '';
  return d.innerHTML;
}

function truncate(str, max) {
  if (!str) return '';
  return str.length <= max ? str : str.substring(0, max) + '...';
}

function getTimeGroup(isoStr) {
  const now = new Date();
  const date = new Date(isoStr);
  const diffDays = Math.floor((now - date) / 86400000);
  if (diffDays === 0) return t('notifications.today');
  if (diffDays === 1) return t('notifications.yesterday');
  if (diffDays < 7) return t('notifications.sidebar.this_week');
  return t('notifications.earlier');
}

function getNotificationIcon(type) {
  const icons = {
    mention: '💬',
    reply: '↩️',
    group_invite: '👥',
    group_request_approved: '✅',
    event_reminder: '📅',
    announcement: '📢',
    poll_closing: '📊',
    ticket_update: '🎫',
    // Both feedback types, so a reply and a status move read the same in the
    // bell. The TYPE KEYS stay `beta_*` — they are stored on every existing
    // notification row and in people's preference rows — but the test tube
    // went with the "Beta Feedback" name on 2026-08-21; it read as "this is
    // a trial", which is exactly the impression the rename exists to remove.
    // ⚠️ An unmapped type falls back to 🔔 — not nothing — so this is polish,
    // unlike the ui-kit icon map where an unknown name renders EMPTY.
    beta_new: '💬',
    beta_feedback: '💬',
    beta_reply: '💬',
    system: '⚙️',
  };
  return icons[type] || '🔔';
}

/**
 * Create the notification bell button for the toolbar.
 * @returns {HTMLElement}
 */
export function createNotificationBell() {
  const btn = document.createElement('button');
  btn.className = 'toolbar-notification-btn';
  btn.setAttribute('aria-label', t('notifications.title'));
  btn.innerHTML = `
    ${BELL_SVG}
    <span class="notification-badge" style="display:none;"></span>
  `;

  btn.addEventListener('click', (e) => {
    e.stopPropagation();
    toggleSidebar();
  });

  ensureStaleStyles();
  bellEl = btn;
  lastSuccessfulPoll = Date.now();

  // Start polling
  pollUnread(btn);
  // 20s, not 60. One indexed COUNT per user — ~3 req/s across the business
  // today, ~10 at 208 people — and a minute is long enough that a popup feels
  // unrelated to whatever caused it.
  pollInterval = setInterval(() => pollUnread(btn), 20000);
  // Re-evaluate stale indicator on a short interval so we surface
  // a failed poll soon after it stops succeeding.
  staleCheckInterval = setInterval(updateStaleIndicator, 15000);

  return btn;
}

/**
 * Create the notification sidebar element.
 * @returns {HTMLElement}
 */
export function createNotificationSidebar() {
  const sidebar = document.createElement('aside');
  sidebar.className = 'notification-sidebar';
  sidebar.id = 'notification-sidebar';
  sidebar.setAttribute('aria-label', t('notifications.title'));

  sidebar.innerHTML = `
    <div class="notification-sidebar-header">
      <h2>${t('notifications.title')}</h2>
      <div class="notification-sidebar-actions">
        <button class="btn-ghost-sm" id="notif-mark-all">${t('notifications.mark_all_read')}</button>
        <button class="notification-sidebar-close" id="notif-close" aria-label="${t('notifications.sidebar.close')}">
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
            <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
          </svg>
        </button>
      </div>
    </div>
    <div class="notification-sidebar-tabs">
      <button class="notification-tab active" data-filter="all">${t('common.all')}</button>
      <button class="notification-tab" data-filter="unread">${t('notifications.unread')}</button>
    </div>
    <div class="notification-sidebar-list" id="notif-list">
      <div style="padding:var(--space-6);text-align:center;">
        <div class="spinner" style="margin:0 auto;"></div>
      </div>
    </div>
    <div class="notification-sidebar-footer">
      <a href="/notifications" data-link class="notification-view-all" id="notif-view-all">${t('notifications.view_all')}</a>
    </div>
  `;

  sidebarEl = sidebar;

  // Event: close button
  sidebar.querySelector('#notif-close').addEventListener('click', closeSidebar);

  // Event: mark all read
  sidebar.querySelector('#notif-mark-all').addEventListener('click', async () => {
    try {
      await api.post('/notifications/read-all');
      updateBadge(0);
      sidebar.querySelectorAll('.notif-item.unread').forEach(el => {
        el.classList.remove('unread');
      });
    } catch (e) { /* silent */ }
  });

  // Event: tab switching
  sidebar.querySelectorAll('.notification-tab').forEach(tab => {
    tab.addEventListener('click', () => {
      sidebar.querySelectorAll('.notification-tab').forEach(t => t.classList.remove('active'));
      tab.classList.add('active');
      loadNotifications(tab.dataset.filter);
    });
  });

  // Event: click notification
  sidebar.querySelector('#notif-list').addEventListener('click', async (e) => {
    const item = e.target.closest('.notif-item');
    if (!item) return;

    const notifId = item.dataset.id;
    const url = item.dataset.url;

    if (item.classList.contains('unread')) {
      try {
        await api.post('/notifications/' + notifId + '/read');
        item.classList.remove('unread');
        const badge = document.querySelector('.notification-badge');
        const current = parseInt(badge?.textContent) || 0;
        updateBadge(Math.max(0, current - 1));
      } catch (e) { /* silent */ }
    }

    if (url) {
      closeSidebar();
      openNotificationUrl(url);
    }
  });

  // Event: view all link
  sidebar.querySelector('#notif-view-all').addEventListener('click', (e) => {
    e.preventDefault();
    closeSidebar();
    router.navigate('/notifications');
  });

  // ESC to close
  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape' && isOpen) closeSidebar();
  });

  return sidebar;
}

function toggleSidebar() {
  if (isOpen) {
    closeSidebar();
  } else {
    openSidebar();
  }
}

function openSidebar() {
  isOpen = true;
  if (sidebarEl) {
    sidebarEl.classList.add('open');
    loadNotifications('all');
  }
}

function closeSidebar() {
  isOpen = false;
  if (sidebarEl) {
    sidebarEl.classList.remove('open');
  }
}

async function loadNotifications(filter = 'all') {
  const listEl = sidebarEl?.querySelector('#notif-list');
  if (!listEl) return;

  listEl.innerHTML = '<div style="padding:var(--space-6);text-align:center;"><div class="spinner" style="margin:0 auto;"></div></div>';

  try {
    const unreadOnly = filter === 'unread' ? '&unread=true' : '';
    const res = await api.get('/notifications?per_page=30' + unreadOnly);
    const notifications = res.notifications || [];

    updateBadge(res.unread_count || 0);

    if (!notifications.length) {
      listEl.innerHTML = `
        <div class="notification-empty">
          <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="var(--text-quaternary)" stroke-width="1.5">
            <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9"/>
            <path d="M13.73 21a2 2 0 0 1-3.46 0"/>
          </svg>
          <p>${t('notifications.sidebar.caught_up')}</p>
        </div>
      `;
      return;
    }

    // Group by time
    const groups = {};
    for (const n of notifications) {
      const group = getTimeGroup(n.created_at);
      if (!groups[group]) groups[group] = [];
      groups[group].push(n);
    }

    let html = '';
    for (const [groupName, items] of Object.entries(groups)) {
      html += `<div class="notif-group-label">${escHtml(groupName)}</div>`;
      for (const n of items) {
        const unreadClass = n.is_read ? '' : ' unread';
        html += `
          <div class="notif-item${unreadClass}" data-id="${n.id}" data-url="${escHtml(n.url || '')}">
            <div class="notif-icon">${getNotificationIcon(n.type)}</div>
            <div class="notif-content">
              <div class="notif-title">${escHtml(n.title)}</div>
              ${n.body ? `<div class="notif-body">${escHtml(truncate(n.body, 100))}</div>` : ''}
              <div class="notif-time">${timeAgo(n.created_at)}</div>
            </div>
            ${!n.is_read ? '<div class="notif-unread-dot"></div>' : ''}
          </div>
        `;
      }
    }

    listEl.innerHTML = html;
  } catch (err) {
    listEl.innerHTML = '<div style="padding:var(--space-6);text-align:center;color:var(--text-tertiary);font-size:var(--text-sm);">' + t('notifications.load_failed') + '</div>';
  }
}

function updateBadge(count) {
  const badges = document.querySelectorAll('.notification-badge');
  badges.forEach(badge => {
    if (count > 0) {
      badge.style.display = '';
      badge.textContent = count > 99 ? '99+' : String(count);
    } else {
      badge.style.display = 'none';
    }
  });
}

// ⚠️ `null` means "not polled yet", which is NOT zero. Starting at 0 would
// treat every notification you already had as newly arrived and replay the
// lot as popups on every page load.
let lastUnread = null;

async function pollUnread() {
  try {
    const res = await api.get('/notifications/unread-count');
    const count = res.unread_count || 0;
    updateBadge(count);
    if (lastUnread !== null && count > lastUnread) announceNew(count - lastUnread);
    lastUnread = count;
    lastSuccessfulPoll = Date.now();
    updateStaleIndicator();
  } catch (e) {
    // Don't update lastSuccessfulPoll — bell will go stale after threshold.
    // ⚠️ And do NOT reset lastUnread: a blip would then look like every
    // unread item had just arrived.
    updateStaleIndicator();
  }
}

/** Pop up whatever just landed. */
async function announceNew(arrived) {
  try {
    const res = await api.get('/notifications', { per_page: Math.min(arrived, 3) });
    const items = (res.notifications || []).filter(n => !n.is_read).slice(0, 3);
    // Oldest first, so the newest ends up nearest the corner.
    items.reverse().forEach(n => showNotificationPopup(n, (clicked) => {
      // Fire-and-forget: failing to mark it read must not block the
      // navigation the person actually asked for.
      api.post(`/notifications/${clicked.id}/read`).catch(() => {});
      pollUnread();
      if (clicked.url) openNotificationUrl(clicked.url);
    }));
  } catch (e) {
    // The badge already moved — that is the honest minimum, and a popup we
    // cannot fill would say nothing useful.
  }
}

export function destroyNotificationSidebar() {
  if (pollInterval) {
    clearInterval(pollInterval);
    pollInterval = null;
  }
  if (staleCheckInterval) {
    clearInterval(staleCheckInterval);
    staleCheckInterval = null;
  }
  isOpen = false;
  sidebarEl = null;
  bellEl = null;
}
